import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as constants from '../common/constants';
import * as JspUtil from '../common/JspUtil';
import * as errorCodes from '../common/errorCodes';
import * as actions from './actionCreators';
import Alert from '@mui/material/Alert';
import ReCAPTCHA from 'react-google-recaptcha';
import { useJspVariables, useAppContext, useLoader, useLoginInput, useDebugRender, usePageTitle } from '../hooks';
import { Dropdown, IceIcon, PageContainer, PageHeader, TextInput, DialogTitleWithClose } from '../components';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import FormHelperText from '@mui/material/FormHelperText';
import MuiDialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

const PageForgotPassword = () => {
    useDebugRender('PageForgotPassword');
    usePageTitle('Forgot Password');
    const location = useLocation();
    const navigate = useNavigate();
    const recaptchaRef = useRef(null);
    const { jspVariables, launcher, panelStyle } = useJspVariables();
    const { getLabelFromJspVariables } = useAppContext(jspVariables);
    const { userName: contextUserName } = useLoginInput();
    const { showLoader, hideLoader } = useLoader();

    const [userName, setUserNameLocal] = useState(contextUserName || '');
    const [appKey, setAppKeyLocal] = useState('');

    const [alert, setAlert] = useState(null);

    // Initialize from JSP globals and location state
    const [isApiUser] = useState(() => {
        if (typeof jspIsApiUser !== 'undefined' && jspIsApiUser === 'Y') return true;
        if (location?.state?.isApiUser) return true;
        return false;
    });

    const ignoreCaptcha = jspVariables.jspDisableCaptcha === 'Y';
    const [captchaResponse, setCaptchaResponse] = useState('');

    const [passwordApps] = useState(() => {
        if (typeof jspPasswordApps !== 'undefined' && jspPasswordApps) {
            return JSON.parse(jspPasswordApps);
        }
        return null;
    });

    const [passwordUsers] = useState(() => {
        const users = {};
        if (typeof jspPasswordApps !== 'undefined' && jspPasswordApps) {
            const parsed = JSON.parse(jspPasswordApps);
            if (parsed && parsed.keys) {
                const keys = JSON.parse(parsed.keys);
                if (keys) {
                    for (let k of keys) {
                        let usr = users[k.app];
                        if (!usr) {
                            usr = [];
                            users[k.app] = usr;
                        }
                        usr.push({ v: k.username, l: k.username });
                    }
                }
            }
        }
        return users;
    });

    const [pageMessage] = useState(() => {
        if (location?.state?.pageMessage) return location.state.pageMessage;
        if (typeof jspPageMessage !== 'undefined' && jspPageMessage) return jspPageMessage;
        return null;
    });

    const [sending, setSending] = useState(false);
    const [errors, setErrors] = useState({});
    const [showSuccess, setShowSuccess] = useState(false);
    const [showAppSelector, setShowAppSelector] = useState(() => {
        return typeof jspPasswordApps !== 'undefined' && !!jspPasswordApps;
    });
    const [passwordApp, setPasswordApp] = useState('');
    const [disableUserName, setDisableUserName] = useState(false);
    const [appUsers, setAppUsers] = useState([]);
    const [userApps, setUserApps] = useState([]);

    const setUserAppsFromData = (apps) => {
        const result = [];
        if (apps) {
            const global = apps.find(x => x.appKey === 'GLOBAL');
            if (global) {
                result.push(global);
                const nonglobal = apps.filter(x => x.appKey !== 'GLOBAL');
                for (let n of nonglobal) {
                    result.push(n);
                }
                setUserApps(result);
                onLocalAppNameChange('GLOBAL');
                return;
            }
            setUserApps(apps);
        }
    };

    useEffect(() => {
        if (JspUtil.isValueAvailable(jspVariables.jspUserId)) {
            setUserNameLocal(jspVariables.jspUserId);
        }
        if (JspUtil.isValueAvailable(jspVariables.jspApp) && jspVariables.jspApp !== constants.APP_LAUNCHER) {
            if (typeof jspPermissions !== 'undefined' && jspPermissions === constants.APP_ICEIM) {
                setAppKeyLocal(constants.APP_ICEIM);
            } else {
                setAppKeyLocal(jspVariables.jspApp);
            }
            setShowAppSelector(false);
        }
        if (launcher) {
            if (jspVariables.jspLoginApp) {
                setAppKeyLocal(jspVariables.jspLoginApp);
            }
            setShowAppSelector(false);
        }
        if (passwordApps) {
            setShowAppSelector(true);
            setUserAppsFromData(passwordApps.apps);
        }
    }, []);

    const getUserAppDescription = (app) => {
        if (app && userApps) {
            const desc = userApps.find(x => x.appKey === app);
            return desc ? desc.description + '/' + app : '';
        }
        return '';
    };

    const onLocalAppNameChange = (val) => {
        setPasswordApp(val);
        if (val === 'GLOBAL') {
            setDisableUserName(true);
            setAppUsers([]);
            if (passwordApps) {
                setUserNameLocal(passwordApps.email);
            }
        } else {
            setDisableUserName(false);
            if (passwordUsers) {
                const userIds = passwordUsers[val];
                setAppUsers(userIds || []);
            }
            if (userName && userName.includes('@')) {
                setUserNameLocal('');
            }
        }
    };

    const resetCaptcha = () => {
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
    };

    const validate = () => {
        const fieldErrors = {};
        if (showAppSelector && !passwordApp) fieldErrors.app = errorCodes.MSG_FIELD_REQUIRED();
        if (!disableUserName && !userName) fieldErrors.email = errorCodes.MSG_FIELD_REQUIRED();
        else if (!disableUserName && !showAppSelector && userName && !userName.includes('@')) fieldErrors.email = errorCodes.MSG_INVALID_EMAIL();
        if (!ignoreCaptcha && !captchaResponse) fieldErrors.captcha = errorCodes.MSG_CAPTCHA_REQUIRED();
        setErrors(fieldErrors);
        return Object.keys(fieldErrors).length === 0;
    };

    const clearFieldError = (field) => {
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const sendForgotPassword = async () => {
        setAlert(null);
        if (!validate()) return;

        const jspLoginApp = jspVariables.jspLoginApp;
        let data = null;
        if (typeof jspData !== 'undefined' && JspUtil.isValueAvailable(jspData)) {
            data = jspData;
        }
        setSending(true);
        showLoader('Sending email...');
        try {
            const app = showAppSelector ? passwordApp : appKey;
            const pwdApp = showAppSelector ? passwordApp : null;
            await actions.sendForgotPassword(userName, app, captchaResponse, redirectUrl, launcher, pwdApp, jspLoginApp, data);
            setSending(false);
            setShowSuccess(true);
        } catch (resp) {
            setSending(false);
            setAlert({ message: resp.message || getLabelFromJspVariables('err_generic'), severity: 'error' });
        } finally {
            hideLoader();
            resetCaptcha();
        }
    };

    const redirectUrl = jspVariables.jspRedirectUrl;
    const backToApplicationEnabled = jspVariables.jspBackToApplicationEnabled !== 'N';

    return (
        <PageContainer id="pageForgotPassword" dataAutomationId="page_forgot_password">
            <MuiDialog open={showSuccess} maxWidth="sm" fullWidth
                aria-labelledby="forgot-password-success-title"
                aria-describedby="forgot-password-success-description">
                <DialogTitleWithClose severity='success' id="forgot-password-success-title"
                    onClose={() => navigate(constants.ROUTE_PAGE_LOGIN)}
                    dataAutomationId="forgot_password_success_title">
                    {getLabelFromJspVariables('alert_email_sent')}
                </DialogTitleWithClose>
                <DialogContent>
                    <DialogContentText id="forgot-password-success-description"
                        sx={{ color: 'text.primary', lineHeight: 1.7 }}>
                        {getLabelFromJspVariables('alert_forgot_email_sent')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    {redirectUrl && backToApplicationEnabled &&
                        <Button variant="outlined"
                            onClick={() => { window.location.href = redirectUrl; }}
                            data-automation-id="forgot_password_back_to_app_btn">
                            {getLabelFromJspVariables('action_return_to_application')}
                        </Button>
                    }
                    <Button variant="contained"
                        onClick={() => navigate(constants.ROUTE_PAGE_LOGIN)}
                        data-automation-id="forgot_password_success_login_btn">
                        {getLabelFromJspVariables('action_login')}
                    </Button>
                </DialogActions>
            </MuiDialog>
            <Box style={panelStyle}>
                <IceIcon launcher={launcher} />
                {isApiUser ?
                    <PageHeader dataAutomationId="forgot_password_header">{getLabelFromJspVariables('title_api_user_change_password')}</PageHeader>
                    :
                    <PageHeader dataAutomationId="forgot_password_header">{getLabelFromJspVariables('label_forgot_password')}</PageHeader>
                }
                <Box>
                    {pageMessage && <Alert severity="warning" sx={{ mb: 1 }}>{pageMessage}</Alert>}
                    {alert && <Alert id="pageForgotPasswordAlert" data-automation-id="page_forgot_password_alert" severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}
                </Box>
                {!isApiUser && <Box>
                    {sending ?
                        <Box>{getLabelFromJspVariables('status_sending_reset_email')}</Box>
                        :
                        <Box id="formForgotPassword" data-automation-id="form_forgot_password">
                            {showAppSelector &&
                                <Box>More than one user accounts use this email: {passwordApps && passwordApps.email}</Box>
                            }
                            {showAppSelector ?
                                <Dropdown selectedValue={passwordApp}
                                    id="forgot_password_app_selector"
                                    name="passwordApp"
                                    dataAutomationId="forgot_password_app_selector"
                                    label={getLabelFromJspVariables('label_select_app_reset_pw')}
                                    labelProperty="description"
                                    valueProperty="appKey"
                                    required
                                    error={!!errors.app}
                                    helperText={errors.app || ''}
                                    options={userApps} placeholder={getLabelFromJspVariables('label_select_application')}
                                    updateValue={val => onLocalAppNameChange(val)} />
                                :
                                <input type="hidden" value={appKey} />
                            }
                            {showAppSelector &&
                                (disableUserName ?
                                    <Box id="user_name_label_global_id1" data-automation-id="user_name_label_global_id1">{getLabelFromJspVariables('note_globalid_no_username')}</Box>
                                    :
                                    <Box id="user_name_label_global_id2" data-automation-id="user_name_label_global_id2">Please enter {getUserAppDescription(passwordApp)} user name</Box>
                                )
                            }
                            {appUsers && appUsers.length > 0 ?
                                <Dropdown selectedValue={userName}
                                    id="forgot_password_user_selector"
                                    name="username"
                                    dataAutomationId="forgot_password_user_selector"
                                    labelProperty="l"
                                    valueProperty="v"
                                    aria-labelledby="user_name_label_id username_label_global_id1 username_label_global_id2"
                                    options={appUsers} placeholder={getLabelFromJspVariables('label_select_username')}
                                    updateValue={val => setUserNameLocal(val)} />
                                :
                                <TextInput
                                    label={getLabelFromJspVariables('label_email')}
                                    id="forgot_password_username_input"
                                    name="username"
                                    type="email"
                                    autoComplete="username"
                                    value={userName}
                                    required={!disableUserName}
                                    disabled={disableUserName}
                                    error={!!errors.email}
                                    helperText={errors.email || ''}
                                    onChange={e => { setUserNameLocal(e.target.value); clearFieldError('email'); }}
                                />
                            }
                            {!ignoreCaptcha && <Box>
                                <ReCAPTCHA sitekey={constants.GOOGLE_RECAPTCHA_V2_SITE_KEY}
                                    ref={recaptchaRef}
                                    onChange={val => setCaptchaResponse(val)}
                                />
                                {errors.captcha && <FormHelperText error>{errors.captcha}</FormHelperText>}
                            </Box>}
                            <Button variant="contained" fullWidth sx={{ mt: 2 }}
                                onClick={sendForgotPassword}
                                data-automation-id="forgot_password_send_email_btn">
                                {getLabelFromJspVariables('action_send_email')}
                            </Button>
                            <Box sx={{ display: 'flex', justifyContent: 'center', m: '0.5rem' }}>
                                <Link component="button" type="button"
                                    onClick={() => navigate(constants.ROUTE_PAGE_LOGIN)}
                                    underline="hover"
                                    data-automation-id="forgot_password_back_link"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                        fontSize: '0.875rem',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        color: 'inherit',
                                    }}>
                                    <ChevronLeftIcon sx={{ fontSize: '1.25rem', color: '#000', strokeWidth: 2, stroke: '#000' }} />
                                    {getLabelFromJspVariables('action_return_to_login')}
                                </Link>
                            </Box>
                        </Box>
                    }
                </Box>
                }
            </Box>
        </PageContainer>
    );
};

export default PageForgotPassword;
